<template>
    <div>
        <div v-if="!userHasGivenMapboxConsent">
            <button
                class="btn btn-primary"
                @click="$store.commit('iframe/userHasGivenMapboxConsent')">
                {{ $t('iFrame.showMap') }}
            </button>
            <p
                class="mt-2 text-muted text-xs"
                style="max-width: 900px">
                <font-awesome-icon
                    icon="info-circle"
                    class="mr-1" />
                <i>{{ $t('iFrame.mapboxLegalNoticeAll') }}</i>
                <a
                    href="https://www.mapbox.com/legal/privacy/"
                    target="_blank"
                    title="https://www.mapbox.com/legal/privacy/">
                    mapbox.com/legal/privacy
                </a>
            </p>
        </div>
        <KeepAlive>
            <BaseMap
                v-if="!showingSingleEvent && userHasGivenMapboxConsent"
                class="map-component"
                :class="{'map-loading': loading}"
                :cluster-source="eventsAsGeoJson"
                :marker-default-name="$t('iFrame.noEventPlaceFound')"
                @clicked-event="$emit('update:showEvent', $event)" />
        </KeepAlive>
    </div>
</template>

<script>
import lazyLoadedComponentsMixin from '@/mixins/lazyLoadedComponentsMixin'

export default {
    name: "IFrameEventMapping",
    mixins: [lazyLoadedComponentsMixin],
    props: {
        events: {
            type: Array,
            required: true
        },
        iFrameType: {
            type: String,
            required: true
        },
        showNumberOfLoadingEntries: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        },
        showingSingleEvent: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        userHasGivenMapboxConsent() {
            return this.$store.state.iframe.consentGiven
        },
        eventsAsGeoJson() {
            const geoJsonFeatureCollection = {
                "type": "FeatureCollection",
                "features": []
            }
            if (!this.events?.length) return geoJsonFeatureCollection
            geoJsonFeatureCollection.features = this.events
                .filter(event => event.location && event.location.latitude && event.location.longitude)
                .map(event => {
                    const coords = event.location ? [event.location.longitude - 0.0002 + Math.random() * 0.0002, event.location.latitude - 0.0002 + Math.random() * 0.0002] : null
                    let date = ""
                    if (event.startDate && (!event.endDate || !this.$moment(event.startDate).isSame(this.$moment(event.endDate), 'day'))) {
                        if (event.endDate || this.$moment(event.startDate).isSame(this.$moment(event.endDate), 'month')) {
                            date += this.$df(this.$moment(event.startDate), 'dateNumericDay')
                        } else {
                            if (event.endDate || this.$moment(event.startDate).isSame(this.$moment(event.endDate), 'year')) {
                                date += this.$df(this.$moment(event.startDate), 'dateShortMonth')
                            } else {
                                date += this.$df(this.$moment(event.startDate), 'dateShort')
                            }
                        }
                    }
                    if (event.startDate && event.endDate && !this.$moment(event.startDate).isSame(this.$moment(event.endDate), 'day'))
                        date += " - "
                    if (event.endDate)
                        date += this.$df(this.$moment(event.endDate), 'dateShort')
                    return {
                        "type": "Feature",
                        "properties": {
                            "id": event.id,
                            "offerNumber": event.offerNumber,
                            "name": event.name,
                            "teaser": event.teaser,
                            "date": date,
                            "location": event.location.name,
                            "lowBarrier": event.location?.lowBarrier === 1,
                            "typeId": event.typeId,
                            "district": event.location.address?.district && event.location.address?.district !== event.location.address?.city ?
                                event.location.address?.district || "" : "",
                            "address": event.location.address?.addressAsString,
                            "image": event.imageSm || event.image || "",
                            "imageLicence": event.imageLicence || ""
                        },
                        "geometry": {
                            "type": "Point",
                            "coordinates": coords
                        }
                    }
                })
            return geoJsonFeatureCollection
        }
    },
    mounted() {
        this.$emit('hook:mounted')
    }
}
</script>

<style lang="scss" scoped>

.map-component {
    aspect-ratio: 1.75;
    width: 100%;
    position: relative;

    &::before {
        content: "";
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        font-size: x-large;
        font-weight: bold;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        pointer-events: none;
        background-color: transparent;
        z-index: 10;
        color: transparent;
        transition: all $map-transition-duration $map-timing-function;
    }

    &.map-loading {
        pointer-events: none;

        &::before {
            content: "Suche...";
            color: $white;
            background-color: rgba($gray-200, 0.5);
        }
    }
}
</style>
